/**
 * CameraForm — ficha de una cámara IP al ESTÁNDAR de la aplicación.
 *
 * Antes el alta y la edición vivían en un `Dialog` (modal). El resto de fichas
 * del CMDB son pantallas completas con topbar (volver, miga, estado, acciones),
 * pestañas fijas, contenido en tarjetas y guardado con ✓/✗. Aquí se replica ese
 * patrón usando los mismos componentes:
 *
 *   .dv-overlay · .dv-topbar · FormCardsShell · .dv-tab-pane · .dv-fieldset
 *
 * Pestañas: **General** (identificación y red), **Ubicación** (2.ª, como en
 * todas las fichas), **Contactos** y **Docs** (solo con la cámara guardada).
 *
 * La ficha abre EN LECTURA (`initialMode='view'`) y se edita con «Editar», como
 * la del rack, el servidor o el switch.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeftIcon,
  CheckIcon,
  VideoCameraIcon,
  XMarkIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import FormCardsShell from './FormCardsShell';
import FormCardsHead from './FormCardsHead';
import FormPager from './common/FormPager';
import FormDirtyDock from './common/FormDirtyDock';
import useNavGuard from '../hooks/useNavGuard';
import TabButton from './TabButton';
import DetailCrumb from './DetailCrumb';
import ContactsPanel from './ContactsPanel';
import DocSmartButton from './DocSmartButton';
import ElementIpPanel from './ElementIpPanel';
import Dialog from './common/Dialog';
import { getServerFormStyle } from '../utils/serverFormStyle';

const FORM_VACIO = {
  id: null,
  name: '',
  code: '',
  recorder_id: '',
  manufacturer: '',
  model: '',
  serial_number: '',
  camera_mode: 'fixed',
  resolution: '',
  fps: '',
  ip_address: '',
  mac_address: '',
  channel: '',
  location_id: '',
  location_floor: '',
  location_room: '',
  poe: true,
  stream_url: '',
  status: 'active',
  notes: '',
};

const MODOS = [
  { value: 'fixed', label: 'Fija' },
  { value: 'ptz', label: 'PTZ' },
  { value: 'dome', label: 'Domo' },
  { value: 'bullet', label: 'Bullet' },
  { value: 'fisheye', label: 'Ojo de pez' },
  { value: 'other', label: 'Otra' },
];

const ESTADOS = [
  { value: 'active', label: 'Activa' },
  { value: 'inactive', label: 'Inactiva' },
  { value: 'maintenance', label: 'Mantenimiento' },
  { value: 'decommissioned', label: 'Dada de baja' },
];

const TONO_ESTADO = {
  active: 'list-badge--on',
  maintenance: 'list-badge--warn',
  decommissioned: 'list-badge--muted',
  inactive: 'list-badge--off',
};

export default function CameraForm({
  camera,
  companyId,
  recorders = [],
  locations = [],
  initialMode = 'view',
  onClose,
  onSaved,
  onDeleted,
  navItems = [],
  navIndex = -1,
  onNavigate,
}) {
  const isCards = getServerFormStyle() === 'cards';
  const [editing, setEditing] = useState(initialMode !== 'view' || !camera?.id);
  const [form, setForm] = useState(() => ({ ...FORM_VACIO, ...(camera || {}) }));
  const [tab, setTab] = useState('general');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [base, setBase] = useState(() => ({ ...FORM_VACIO, ...(camera || {}) }));
  const nameRef = useRef(null);

  useEffect(() => {
    const siguiente = { ...FORM_VACIO, ...(camera || {}) };
    setForm(siguiente);
    setBase(siguiente);
    setEditing(initialMode !== 'view' || !camera?.id);
  }, [camera, initialMode]);

  const sf = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const dirty = useMemo(
    () => Object.keys({ ...FORM_VACIO, ...form }).some((key) => String(form[key] ?? '') !== String(base[key] ?? '')),
    [form, base],
  );

  // Cambios sin guardar al pasar de ficha o cerrar.
  const navGuard = useNavGuard({
    dirty,
    saving,
    entity: 'esta cámara',
    actionLabel: 'pasas a otra cámara o cierras la ficha',
    save: () => guardar({ keepEditing: editing }),
  });

  const statusLabel = ESTADOS.find((e) => e.value === form.status)?.label || form.status;  const recorderName = recorders.find((r) => String(r.id) === String(form.recorder_id))?.name
    || form.recorder_name || '';
  const locationName = locations.find((l) => String(l.id) === String(form.location_id))?.name
    || form.location_name || '';

  const guardar = useCallback(async ({ keepEditing = false } = {}) => {
    if (!form.name.trim()) {
      setError('El nombre es obligatorio');
      setTab('general');
      return false;
    }
    setSaving(true);
    setError('');
    try {
      const payload = { ...form };
      ['id', 'created_at', 'updated_at', 'company_id', 'recorder_name', 'location_name',
        'status_label', 'camera_mode_label'].forEach((key) => delete payload[key]);
      payload.recorder_id = form.recorder_id || null;
      payload.location_id = form.location_id || null;

      const guardado = form.id
        ? await api.updateCamera(form.id, payload, companyId)
        : await api.createCamera(payload, companyId);
      setForm({ ...FORM_VACIO, ...guardado });
      setBase({ ...FORM_VACIO, ...guardado });
      if (!keepEditing) setEditing(false);
      onSaved?.(guardado);
      return true;
    } catch (err) {
      setError(err.message || 'No se pudo guardar la cámara');
      return false;
    } finally {
      setSaving(false);
    }
  }, [form, companyId, onSaved, editing]);

  const descartar = () => {
    if (!form.id) { onClose?.(); return; }
    setForm({ ...base });
    setEditing(false);
    setError('');
  };

  const eliminar = async () => {
    if (!form.id) return;
    try {
      await api.deleteCamera(form.id, companyId);
      setConfirmDelete(false);
      onDeleted?.(form);
    } catch (err) {
      setError(err.message || 'No se pudo eliminar la cámara');
      setConfirmDelete(false);
    }
  };

  const tabs = [
    { key: 'general', label: 'General' },
    { key: 'red', label: 'Red' },
    { key: 'ubicacion', label: 'Ubicación' },
    ...(form.id ? [{ key: 'contactos', label: 'Contactos' }] : []),
  ];

  /** Campo del estándar de tarjetas: `<div class="sf2-field">` con su etiqueta. */
  const campo = (label, key, placeholder, extraProps = {}) => (
    <div className="sf2-field">
      <label>{label}</label>
      {editing
        ? (
          <input
            value={form[key] ?? ''}
            onChange={(e) => sf(key, e.target.value)}
            placeholder={placeholder}
            {...extraProps}
          />
        )
        : <div className="v">{form[key] || '—'}</div>}
    </div>
  );

  return (
    <div className="dv-overlay">
      {/* ── Topbar: volver, miga, estado y acciones. Sin contadores ni estados duplicados. ── */}
      <div className="dv-topbar">
        <button className="dv-back-btn" type="button" onClick={onClose}>
          <ArrowLeftIcon style={{ width: 15, height: 15 }} />
          Cámaras
        </button>
        <DetailCrumb label={form.name || 'Nueva cámara'} />
        <FormPager
          items={navItems}
          index={navIndex}
          label="Cámara"
          disabled={saving}
          onChange={(destino) => navGuard.request(() => onNavigate?.(destino))}
        />
        {navGuard.dialog}
        <div className="dv-topbar-spacer" />
        {error && <span style={{ color: '#dc2626', fontSize: '0.78rem', maxWidth: 320 }}>⚠ {error}</span>}
        <div className="dv-topbar-actions">
          <span className={`list-badge ${TONO_ESTADO[form.status] || 'list-badge--muted'}`}>{statusLabel}</span>
          {!editing ? (
            <>
              {form.id && (
                <button className="dv-btn dv-btn-secondary" type="button" onClick={() => setEditing(true)}>
                  <PencilSquareIcon style={{ width: 14, height: 14 }} /> Editar
                </button>
              )}
              <button className="dv-btn dv-btn-secondary" type="button" onClick={onClose}>
                <XMarkIcon style={{ width: 14, height: 14 }} /> Cerrar
              </button>
            </>
          ) : (
            <>
              {form.id && (
                <button
                  className="dv-btn dv-btn-secondary"
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  title="Eliminar la cámara"
                >
                  <TrashIcon style={{ width: 14, height: 14 }} /> Eliminar
                </button>
              )}
              <button className="dv-btn dv-btn-secondary" type="button" onClick={descartar} disabled={saving}>
                <XMarkIcon style={{ width: 14, height: 14 }} /> Descartar
              </button>
              <button
                className="dv-btn dv-btn-primary"
                type="button"
                onClick={guardar}
                disabled={saving || !dirty}
                title={dirty ? 'Guardar los cambios' : 'Sin cambios'}
              >
                {saving ? <span className="spinner-border spinner-border-sm" /> : <CheckIcon style={{ width: 14, height: 14 }} />}
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="dv-body">
        <form className={`dv-main${isCards ? ' sf2-main' : ''}`} onSubmit={(e) => e.preventDefault()}>
          <FormCardsShell
            isCards={isCards}
            header={isCards ? (
              <FormCardsHead
                icon={<VideoCameraIcon style={{ width: 20, height: 20 }} />}
                title={form.name}
                onTitleChange={(value) => sf('name', value)}
                placeholder="Nombre de la cámara…"
                editing={editing}
                saving={saving}
                onSave={guardar}
                onCancel={descartar}
                onEdit={() => setEditing(true)}
                crumb={[recorderName, locationName].filter(Boolean).join(' › ')}
                badges={[{ text: statusLabel, color: form.status === 'active' ? '#16a34a' : '#64748b' }]}
                chips={[
                  ...(form.ip_address ? [{ value: form.ip_address, label: 'IP' }] : []),
                  ...(form.channel ? [{ value: form.channel, label: 'canal' }] : []),
                ]}
                dirty={dirty}
                inline
                titleDirty={dirty}
                onTitleCommit={() => { if (form.id) guardar(); }}
                onTitleReset={() => setForm({ ...base })}
                extra={form.id ? (
                  <DocSmartButton
                    elementType="camera"
                    elementId={form.id}
                    elementName={form.name}
                    companyId={companyId}
                  />
                ) : null}
              />
            ) : (
              <div className="dv-record-header">
                <span className="dv-record-icon"><VideoCameraIcon style={{ width: 18, height: 18 }} /></span>
                <input
                  ref={nameRef}
                  className="dv-record-title"
                  value={form.name}
                  onChange={(e) => sf('name', e.target.value)}
                  placeholder="Nombre de la cámara…"
                  disabled={!editing}
                />
              </div>
            )}
            tabs={(
              <div className="dv-tabs-nav">
                {tabs.map((t) => (
                  <TabButton
                    key={t.key}
                    type="button"
                    className={`dv-tab-btn${tab === t.key ? ' active' : ''}`}
                    onClick={() => setTab(t.key)}
                  >
                    {t.label}
                  </TabButton>
                ))}
              </div>
            )}
          >
            <fieldset className="dv-fieldset" disabled={!editing}>
              {tab === 'general' && (
                <div className="dv-tab-pane sf2-pane">
                  <div className="sf2-grid sf2-grid--2">
                    <div className="sf2-card">
                      <h3>Identificación</h3>
                      <div className="sf2-fields" style={{ '--n': 2 }}>
                        {campo('Código', 'code', 'CAM-0001')}
                        {campo('Fabricante', 'manufacturer', 'Hikvision, Dahua, Axis…')}
                        {campo('Modelo', 'model', 'DS-2CD2143G2')}
                        {campo('Nº de serie', 'serial_number', 'S/N')}
                        <div className="sf2-field">
                          <label>Tipo</label>
                          {editing ? (
                            <select value={form.camera_mode || 'fixed'} onChange={(e) => sf('camera_mode', e.target.value)}>
                              {MODOS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                            </select>
                          ) : <div className="v">{MODOS.find((m) => m.value === form.camera_mode)?.label || '—'}</div>}
                        </div>
                        <div className="sf2-field">
                          <label>Estado</label>
                          {editing ? (
                            <select value={form.status} onChange={(e) => sf('status', e.target.value)}>
                              {ESTADOS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                            </select>
                          ) : <div className="v">{statusLabel}</div>}
                        </div>
                        <div className="sf2-field" style={{ gridColumn: '1 / -1' }}>
                          <label>Grabador</label>
                          {editing ? (
                            <select
                              value={form.recorder_id || ''}
                              onChange={(e) => sf('recorder_id', e.target.value)}
                            >
                              <option value="">— sin grabador —</option>
                              {recorders.map((r) => (
                                <option key={r.id} value={r.id}>{r.name}</option>
                              ))}
                            </select>
                          ) : <div className="v">{recorderName || '—'}</div>}
                        </div>
                      </div>
                    </div>

                    <div className="sf2-card">
                      <h3>Notas</h3>
                      <div className="sf2-fields" style={{ '--n': 1 }}>
                        <div className="sf2-field">
                          <label>Observaciones</label>
                          {editing
                            ? (
                              <textarea
                                rows={8}
                                value={form.notes || ''}
                                onChange={(e) => sf('notes', e.target.value)}
                                placeholder="Dónde está, a qué apunta, incidencias…"
                              />
                            )
                            : <div className="v" style={{ whiteSpace: 'pre-wrap' }}>{form.notes || '—'}</div>}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {tab === 'red' && (
                <div className="dv-tab-pane sf2-pane">
                  <div className="sf2-grid sf2-grid--2">
                    <div className="sf2-card">
                      <h3>Conectividad</h3>
                      <div className="sf2-fields" style={{ '--n': 1 }}>
                        {campo('MAC', 'mac_address', 'AA:BB:CC:DD:EE:FF')}
                        <div className="sf2-field">
                          <label>IP (IP principal – se sincroniza con IPAM)</label>
                          {editing
                            ? (
                              <input
                                value={form.ip_address || ''}
                                onChange={(e) => sf('ip_address', e.target.value)}
                                placeholder="10.0.0.50"
                              />
                            )
                            : <div className="v">{form.ip_address || '—'}</div>}
                        </div>
                        {campo('Canal del grabador', 'channel', '1', { type: 'number' })}
                      </div>
                    </div>

                    <div className="sf2-card">
                      <h3>Vídeo y alimentación</h3>
                      <div className="sf2-fields" style={{ '--n': 2 }}>
                        {campo('Resolución', 'resolution', '1080p · 4 MP')}
                        {campo('FPS', 'fps', '25', { type: 'number' })}
                        <div className="sf2-field" style={{ gridColumn: '1 / -1' }}>
                          <label>PoE</label>
                          <label className="sf2-switch sf2-switch--sm">
                            <input
                              type="checkbox"
                              checked={Boolean(form.poe)}
                              onChange={(e) => sf('poe', e.target.checked)}
                              disabled={!editing}
                            />
                            <span className="sf2-switch-track"><span className="sf2-switch-thumb" /></span>
                            <span className="sf2-switch-label">{form.poe ? 'Alimentada por PoE' : 'Sin PoE'}</span>
                          </label>
                        </div>
                        <div className="sf2-field" style={{ gridColumn: '1 / -1' }}>
                          <label>URL del stream</label>
                          {editing
                            ? <input value={form.stream_url || ''} onChange={(e) => sf('stream_url', e.target.value)} placeholder="rtsp://…" />
                            : <div className="v">{form.stream_url || '—'}</div>}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Mismo bloque que estación de trabajo, impresora o servidor:
                      las IPs del equipo se ven y se tocan aquí, contra el IPAM.
                      En alta sale deshabilitado hasta guardar la cámara. */}
                  <div className="dv-section" style={{ marginTop: 16 }}>
                    <div className="dv-section-title">IPAM — Asignaciones de IP</div>
                    <ElementIpPanel
                      objectType="camera"
                      objectId={form.id}
                      companyId={companyId}
                      elementName={form.name}
                    />
                  </div>
                </div>
              )}

              {tab === 'ubicacion' && (
                <div className="dv-tab-pane sf2-pane">
                  <div className="sf2-card">
                    <h3>Ubicación</h3>
                    <div className="sf2-fields" style={{ '--n': 2 }}>
                      <div className="sf2-field" style={{ gridColumn: '1 / -1' }}>
                        <label>Ubicación / centro</label>
                        {editing ? (
                          <select value={form.location_id || ''} onChange={(e) => sf('location_id', e.target.value)}>
                            <option value="">— sin ubicación —</option>
                            {locations.map((l) => (
                              <option key={l.id} value={l.id}>{l.name}</option>
                            ))}
                          </select>
                        ) : <div className="v">{locationName || '—'}</div>}
                      </div>
                      {campo('Planta', 'location_floor', 'Planta 1')}
                      {campo('Sala / zona', 'location_room', 'Pasillo norte')}
                    </div>
                    <p className="sf2-hint" style={{ marginTop: 10 }}>
                      La posición fina sobre el plano se ajusta en «Mapa de planta · Cámaras».
                    </p>
                  </div>
                </div>
              )}

              {tab === 'contactos' && form.id && (
                <div className="dv-tab-pane sf2-pane">
                  <div className="dv-section">
                    <div className="dv-section-title">Contactos</div>
                    <ContactsPanel
                      companyId={companyId}
                      objectType="camera"
                      objectId={form.id}
                      objectName={form.name}
                    />
                  </div>
                </div>
              )}
            </fieldset>
            <FormDirtyDock
              show={dirty}
              saving={saving}
              onDiscard={descartar}
              onSave={() => guardar()}
            />
          </FormCardsShell>
        </form>
      </div>

      {confirmDelete && (
        <Dialog
          title="Eliminar cámara"
          onClose={() => setConfirmDelete(false)}
          footer={(
            <>
              <button type="button" className="list-btn-secondary" onClick={() => setConfirmDelete(false)}>Cancelar</button>
              <button type="button" className="list-btn-danger" onClick={eliminar}>Eliminar</button>
            </>
          )}
        >
          <p>
            ¿Eliminar la cámara «{form.name}»? También se libera su IP en el IPAM.
          </p>
        </Dialog>
      )}
    </div>
  );
}
